import { auth, db } from '../firebase/firebaseConfig';
import { doc, setDoc, getDoc, collection, getDocs, runTransaction } from 'firebase/firestore';
import { derivePracticeAnalysis, recentCasesForMode } from './statsUtils';
import { logError } from './logging';
import { getCollectionName } from '../firebase/collections';
import { getAlgorithmSet } from '../constants/algorithms';
import { PRACTICE_MODES } from '../constants/modes';

// 16 random bytes, so two solves in the same millisecond are two documents.
const uniqueEventSuffix = () => {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const addAlgorithmTime = async (algorithmId, time, mode) => {
  if (!auth.currentUser) {
    console.warn('No user logged in');
    return;
  }
  
  const userId = auth.currentUser.uid;
  const timestamp = new Date().toISOString();
  const eventId = `${timestamp}_${uniqueEventSuffix()}`;
  const timeEntryRef = doc(db, `users/${userId}/times/${eventId}`);

  await setDoc(timeEntryRef, {
    algorithmId,
    time,
    timestamp,
    mode
  });

  return eventId;
};

export const loadSolveEvents = async () => {
  if (!auth.currentUser) return [];

  const timesRef = collection(db, 'users', auth.currentUser.uid, 'times');
  const snapshot = await getDocs(timesRef);
  return snapshot.docs
    .filter((eventDoc) => !eventDoc.metadata?.hasPendingWrites)
    .map((eventDoc) => ({
      id: eventDoc.id,
      algorithmId: eventDoc.data().algorithmId,
      time: eventDoc.data().time,
      timestamp: eventDoc.data().timestamp,
      mode: eventDoc.data().mode,
    }));
};

const casesForSet = (setType) => Object.values(getAlgorithmSet(setType).cases);

export const practiceCasesByMode = () => ({
  [PRACTICE_MODES.OLL.EXECUTION]: casesForSet('OLL'),
  [PRACTICE_MODES.PLL.EXECUTION]: casesForSet('PLL'),
  [PRACTICE_MODES.OLL.RECOGNITION]: casesForSet('OLL'),
  [PRACTICE_MODES.PLL.RECOGNITION]: casesForSet('PLL'),
});

export const loadPracticeAnalysis = async () => {
  const events = await loadSolveEvents();
  return derivePracticeAnalysis(events, practiceCasesByMode());
};

export const loadRecentCases = async (mode) => {
  const events = await loadSolveEvents();
  return recentCasesForMode(events, mode, practiceCasesByMode()[mode] || []);
};

export const getAlgorithmStats = async (algorithmId, mode, userId) => {
  if (!algorithmId) return null;
  
  try {
    const statsRef = doc(db, `users/${userId}/algorithms/${algorithmId}/data/stats`);
    const statsDoc = await getDoc(statsRef);
    return statsDoc.exists() ? statsDoc.data() : null;
  } catch (error) {
    console.error("Error in getAlgorithmStats:", error);
    throw error;
  }
};

// Issue #15 removed deleteLastAlgorithmTime. It deleted one times document, then
// called the stats writers with null, which appended null, incremented attempts,
// and could set the fastest time to zero. Issue #18 must replace it; do not restore it.

export const saveTime = async (userId, algorithmId, time, mode) => {
  const collectionName = getCollectionName(mode);
  const statsRef = doc(db, collectionName, userId);
  
  try {
    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      if (!statsDoc.exists()) {
        transaction.set(statsRef, {
          times: {
            [algorithmId]: [time]
          }
        });
      } else {
        const data = statsDoc.data();
        const times = data.times[algorithmId] || [];
        times.push(time);
        transaction.update(statsRef, {
          [`times.${algorithmId}`]: times
        });
      }
    });
  } catch (error) {
    logError("Error saving time:", error);
  }
};

export const getStats = async (userId, algorithmId, mode) => {
  const collectionName = getCollectionName(mode);
  const statsRef = doc(db, collectionName, userId);
  
  try {
    const statsDoc = await getDoc(statsRef);
    if (!statsDoc.exists()) return null;
    
    const data = statsDoc.data();
    return data.times[algorithmId] || [];
  } catch (error) {
    logError("Error getting stats:", error);
    return null;
  }
};

